import { AvatarComponent } from 'design/avatar/avatarComponents';
import { createClassName } from 'utils/createClassName';
import Croppie from 'croppie';
import React from 'react';
import 'croppie/croppie.css';

import { ReactComponent as UploadIcon } from './avatarInputUploadIcon.svg';
import './avatarInputStyles.scss';
import { AvatarInputValue } from 'types/avatarInputTypes';

type Props = {
  id?: string;
  name: string;
  value: null | AvatarInputValue;
  fallbackURL: null | string;
  placeholder: React.ReactNode;
  disabled: boolean;
  invalid: boolean;
  autoFocus?: boolean;
  hint?: React.ReactNode;
  onValueChange(nextValue: AvatarInputValue): void;
};

export const AvatarInputComponent = (props: Props) => {
  const {
    id,
    name,
    value,
    fallbackURL,
    placeholder,
    disabled,
    invalid,
    autoFocus,
    hint,
    onValueChange,
  } = props;

  const inputNodeRef = React.useRef<HTMLInputElement>(null);

  const cropperNodeRef = React.useRef<HTMLInputElement>(null);

  const cropperRef = React.useRef<null | Croppie>(null);

  const onChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.currentTarget;

      if (files) {
        for (let file of Array.from(files)) {
          const { current: cropper } = cropperRef;

          if (cropper) {
            const nextValue: AvatarInputValue = {
              rawFile: file,
              async readEditedFile() {
                return cropper
                  .result({ type: 'blob', size: { width: 500, height: 500 } })
                  .then((blob) => {
                    const editedFile = new File([blob], file.name, {
                      type: blob.type,
                    });

                    return editedFile;
                  });
              },
            };

            onValueChange(nextValue);
          }
        }

        event.target.value = '';
      }
    },
    [onValueChange]
  );

  const classNameConstructor = createClassName('avatarInput');

  const hasPreview = !invalid && (value !== null || fallbackURL !== null);

  const wrapperClassName = classNameConstructor('wrapper', {
    invalid,
    hasPreview,
  });

  // WARNING: Croppie вешает на элемент свой класс, динамические классы его стирают из-за чего слетает разметка
  const cropperClassName = classNameConstructor('cropper');

  const previewClassName = classNameConstructor('preview', {
    invalid,
    hasPreview,
  });

  const fallbackClassName = classNameConstructor('fallback', {
    invalid,
    hasPreview,
  });

  const inputClassName = classNameConstructor('input', {
    invalid,
    hasPreview,
  });

  const infoClassName = classNameConstructor('info', {
    invalid,
    hasPreview,
  });

  const fallbackPlaceholderClassName = classNameConstructor(
    'fallbackPlaceholder',
    {
      invalid,
      hasPreview,
    }
  );

  const infoPlaceholderClassName = classNameConstructor('infoPlaceholder', {
    invalid,
    hasPreview,
  });

  const hintClassName = classNameConstructor('hint', {
    invalid,
    hasPreview,
  });

  React.useEffect(() => {
    const { current: cropperNode } = cropperNodeRef;

    if (cropperNode) {
      const croppie = new Croppie(cropperNode, {
        viewport: {
          width: 219,
          height: 219,
          type: 'circle',
        },
        boundary: {
          width: 223,
          height: 223,
        },
        enableOrientation: true,
      });

      cropperRef.current = croppie;

      return () => {
        croppie.destroy();
      };
    }
  }, []);

  React.useEffect(() => {
    const { current: cropper } = cropperRef;

    if (cropper && value) {
      const { rawFile } = value;

      const valueURL = URL.createObjectURL(rawFile);

      cropper.bind({ url: valueURL });

      return () => {
        URL.revokeObjectURL(valueURL);
      };
    }
  }, [value]);

  const cropperIsShown = value !== null && !invalid;

  const placeholderIsShown =
    (value === null && fallbackURL === null) || invalid;

  const fallbackIsShown = !invalid && value === null && fallbackURL;

  return (
    <div className={wrapperClassName}>
      <div className={previewClassName}>
        <div
          className={cropperClassName}
          ref={cropperNodeRef}
          style={{ display: cropperIsShown ? undefined : 'none' }}
        />
        {fallbackIsShown && fallbackURL && (
          <AvatarComponent url={fallbackURL} alt="value" />
        )}
        {placeholderIsShown && (
          <label htmlFor={id} className={fallbackClassName}>
            <UploadIcon />
            <div className={fallbackPlaceholderClassName}>{placeholder}</div>
          </label>
        )}
        <input
          id={id}
          name={name}
          ref={inputNodeRef}
          type="file"
          multiple={false}
          autoFocus={autoFocus}
          disabled={disabled}
          className={inputClassName}
          onChange={onChange}
        />
      </div>
      <div className={infoClassName}>
        <label htmlFor={id}>
          <div className={infoPlaceholderClassName}>{placeholder}</div>
        </label>
        <div className={hintClassName}>{hint}</div>
      </div>
    </div>
  );
};
